import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Calendar as CalIcon, Clock } from "lucide-react";

const TIMES = ["09:00", "10:30", "12:00", "15:00", "16:30", "18:00"];

function getNextDays(n = 14) {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= n; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    out.push(d);
  }
  return out;
}

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const days = getNextDays(14);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please pick a date and a time.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/bookings", { ...form, date: date.toISOString().slice(0, 10), time });
      setDone(true);
      toast.success("Session requested. Anant will confirm shortly.");
    } catch {
      toast.error("Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="booking-success">
        <div className="font-script text-primary-ub text-4xl">Thank you.</div>
        <h1 className="font-serif text-5xl mt-4">Your request is with Anant.</h1>
        <p className="mt-4 text-ub-2">You'll receive a personal note within 24 hours to confirm your session.</p>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="text-sm uppercase tracking-[0.25em] text-primary-ub">1:1 Coaching</div>
      <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02] tracking-tighter">
        A quiet hour<br/> <span className="italic text-primary-ub">just for you.</span>
      </h1>
      <p className="mt-4 text-lg text-ub-2 max-w-2xl">
        Pick a time that feels right. Anant personally reviews every booking and returns with a confirmation.
      </p>
      <form onSubmit={submit} className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8" data-testid="booking-form">
        <div className="md:col-span-7 bg-ub-alt border border-ub rounded-[2rem] p-8">
          <div className="flex items-center gap-2 text-ub-2 mb-4"><CalIcon size={18} className="text-primary-ub" /> <span className="font-medium">Choose a date</span></div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {days.map((d) => {
              const active = date && d.toDateString() === date.toDateString();
              return (
                <button
                  type="button"
                  key={d.toISOString()}
                  onClick={() => setDate(d)}
                  data-testid={`date-${d.toISOString().slice(0, 10)}`}
                  className={`p-3 rounded-2xl border text-sm transition-all ${
                    active ? "bg-ub-dark text-white border-ub-dark" : "bg-ub border-ub hover:border-[var(--ub-primary)]"
                  }`}
                >
                  <div className="text-xs uppercase opacity-70">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                  <div className="text-lg font-serif">{d.getDate()}</div>
                  <div className="text-xs opacity-70">{d.toLocaleDateString(undefined, { month: "short" })}</div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 text-ub-2 mt-8 mb-4"><Clock size={18} className="text-primary-ub" /> <span className="font-medium">Choose a time</span></div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {TIMES.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setTime(t)}
                data-testid={`time-${t}`}
                className={`px-3 py-2.5 rounded-full text-sm border transition-all ${
                  time === t ? "bg-primary-ub text-white border-primary-ub" : "bg-ub border-ub hover:border-[var(--ub-primary)]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-5 bg-ub border border-ub rounded-[2rem] p-8 space-y-3">
          <div className="font-serif text-2xl mb-2">Your details</div>
          <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" className="w-full px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" className="w-full px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
          <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" className="w-full px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
          <textarea rows={4} placeholder="What would you like to explore?" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="booking-topic" className="w-full px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors resize-none" />
          <button disabled={busy} data-testid="booking-submit-btn" className="w-full bg-primary-ub text-white rounded-full py-4 font-medium hover:bg-[var(--ub-primary-hover)] transition-colors disabled:opacity-50">
            {busy ? "Requesting…" : "Request session"}
          </button>
        </div>
      </form>
    </div>
  );
}
